import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosQualityService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type { CreateQcReportPayload } from '@/types/roros/quality';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosQualityReports(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.quality.reports, params] as const,
    queryFn: () => RorosQualityService.listReports(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosQualityReport(id: string) {
  return useQuery({
    queryKey: rorosKeys.quality.report(id),
    queryFn: () => RorosQualityService.getReport(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosFttSummary(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.quality.ftt, params] as const,
    queryFn: () => RorosQualityService.getFttSummary(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosFttByDomain(domainId: string) {
  return useQuery({
    queryKey: rorosKeys.quality.fttDomain(domainId),
    queryFn: () => RorosQualityService.getFttByDomain(domainId),
    enabled: !!domainId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosQualityReportCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQcReportPayload) => RorosQualityService.createReport(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.quality.reports });
      qc.invalidateQueries({ queryKey: rorosKeys.quality.ftt });
    },
  });
}
